import { captureException } from '@sentry/browser';
import app from 'Cloud/Application/app';
import config from 'Cloud/config';
import { plural } from 'lib/plural';
import { path } from 'ramda';
import React from 'react';
import { IS_CLOUD_BANNED, IS_CLOUD_CREATED, IS_CLOUD_LOCKED, IS_REACT_PAGE } from 'reactApp/appHelpers/configHelpers';
import { isModifiedSharingWindow, isWopiCoEdit } from 'reactApp/appHelpers/featuresHelpers';
import { EFrom } from 'reactApp/components/SharingWindow/Sharing.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { createNewAutoDeleteFolder, moveRequest, openPublishDialog, removeRequest } from 'reactApp/modules/modifying/modifying.actions';
import { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { reDownloadController, ReDownloadEntry } from 'reactApp/modules/requiredAuthorization/reDownloadController';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    isDomainFolder as isDomainFolderStorage,
    isMountedOrSharedFolder,
    isReadOnlyMountedOrShared,
} from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isDownloadByUrl } from 'reactApp/modules/userQuotaCleaner/helpers/isDownloadByUrl';
import type { QuotaCleanerItem } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import { store as reduxStore } from 'reactApp/store';
import { noop, noopVoid } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';
import { getSize, getSizeInteger } from 'reactApp/utils/sizeHelper';

const { renderComfirmationDialog } = process.env.SSR
    ? ({} as any)
    : require('reactApp/components/BaseConfirmDialog/BaseConfirmDialog.helpers');

export const goTo = (
    { id = null, storage = '', params = null }: any,
    forceReload?: any,
    fromLocation?: any,
    fromHistory?: any,
    isPopstate?: any,
    forceNoDiff?: any
) => app.goTo({ id, storage, params }, forceReload, fromLocation, fromHistory, isPopstate, forceNoDiff);

// если текущую папку удалили / переместили, то мы можем оказаться в хомяке, поэтому фоллбек на app.current()
export const getCurrentFolder = () => (app.currentFolder && app.currentFolder()) || (app.current && app.current());
export const getCurrentItem = () => {
    const id = path(['id'], app && app.getState && app.getState());
    if (id) {
        return app.get(id);
    }
};

export const isHome = () => app.isHome();
export const isPublic = () => app.isPublic();
export const isRoot = () => {
    const currentFolder = getCurrentFolder();
    return currentFolder && currentFolder.id === '/';
};
export const isDomainFolder = (item) =>
    item && item.isFolder && ((app?.isDomainFolder && app?.isDomainFolder(item)) || isDomainFolderStorage(item));

const modifyItemInTree = (id, data) => {
    const newItem = app.get(id, app.isEditor() ? null : 'home');

    if (!newItem || !data) {
        // так как он еще не был загружен
        return;
    }

    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            newItem[key] = data[key];
        }
    }

    app.emit('modifying:update', newItem);
};
export const updateItemFavoriteFlag = (id, isInFavorites) => !IS_REACT_PAGE && modifyItemInTree(id, { isInFavorites });

export const getCurrentState = () => app.getState && app.getState();

// eslint-disable-next-line no-useless-call
export const appOn = (...args) => app.on.call(app, ...args);
// eslint-disable-next-line no-useless-call
export const appOff = (...args) => app.off.call(app, ...args);

export const getPublicUrl = (item, isAlbum = false) => app.publicUrl(item, isAlbum);

export const publishSocial = (socialId, item) => app.social.publishItem(socialId, item);
export const socialList = () => app.social.list || [];

export const downloadFile = (id) => app.download(id);
export const downloadItem = ({ itemOrId, useDownloadUrl = false, storage = null, forceDownload = null, fromViewer = false }: any) => {
    const isQuotaCleaner = storage === EStorageType.quotaCleaner;
    const isDownloadUrl = useDownloadUrl || (isQuotaCleaner && isDownloadByUrl(itemOrId as QuotaCleanerItem));

    /* tempexp_15344-next-line */
    reDownloadController.setFutureParams(ReDownloadEntry.AppHelpers, {
        itemOrId,
        useDownloadUrl,
        storage,
        forceDownload,
        fromViewer,
    });

    if (itemOrId.isFolder) {
        app.downloadItems(itemOrId.name, itemOrId, null);
    } else {
        app.download(itemOrId, forceDownload, isDownloadUrl, storage, fromViewer);
    }
};

export const downloadItemsZip = ({ items, name = 'archive', forceDownload = null }) => app.downloadItems(name, items, forceDownload);
export const downloadByUrl = (url, name, storage) => app.downloadItem(url, name, storage);

export const isReadOnly = (item) =>
    (app.currentFolder && app.currentFolder() && app.isReadOnlyMountedOrShared(path(['id'], app.currentFolder()))) ||
    isReadOnlyMountedOrShared(reduxStore.getState(), item);

export const getSharedAndMountedStatus = (item) => item && app.isMountedOrSharedFolder && app.isMountedOrSharedFolder(item);
export const isSharedOrMountedStatus = (item) => {
    const isSharedOrMounted = isMountedOrSharedFolder(reduxStore.getState(), item);
    return isSharedOrMounted && (isSharedOrMounted.isMounted || isSharedOrMounted.isShared);
};

export const isMountedFolder = (item) => app.isMountedFolder(item);
export const isPublicFolder = () => !!config.get('PUBLIC_FOLDER');

export const isGalleryStorage = () => getStorage(getCurrentStorage(reduxStore.getState())).isGallery;

export const getDownloadItemSize = (items) => {
    if (!items.length) {
        return '';
    }

    if (!items.every((item) => typeof item.size === 'number')) {
        return;
    }

    if (items.length === 1) {
        return getSize(getSizeInteger(items[0].size));
    }

    return getSize(items.reduce((acc, cur) => acc + getSizeInteger(cur.size), 0));
};

export const openEditor = (item, editor = '') => app.editor.openEditor(item, editor);
export const getEditorsFor = (item) => app.editor.getEditorsFor(item);
export const isFileForOpenInEditor = (item) => {
    if (isPublic() || isReadOnly(item) || item.isFile || item.isReadOnly) {
        return false;
    }

    const editors = app.editor && getEditorsFor(item);

    return editors && editors.some((editor) => ['R7'].includes(editor.id));
};

export const moveItemsDialog = (items, destination) => {
    if (!items || !destination) {
        return;
    }
    const isSharedOrMounted = isSharedOrMountedStatus(destination);
    const count = {
        folders: items.filter((item) => item.isFolder).length,
        files: 0,
    };
    count.files = items.length - count.folders;

    let text = '';
    if (items.length === 1) {
        text = `${count.folders ? 'папку' : 'файл'} «${items[0].name}»`;
    } else {
        let foldersText = '';
        if (count.folders) {
            foldersText = `${count.folders} ${['выбранную', 'выбранные', 'выбранных'][plural(count.folders)]} ${
                ['папку', 'папки', 'папок'][plural(count.folders)]
            }`;
        }
        const and = count.files && count.folders ? ' и ' : '';
        let filesText = '';
        if (count.files) {
            filesText = `${count.files} ${count.folders ? '' : ['выбранный', 'выбранных', 'выбранных'][plural(count.folders)]} ${
                ['файл', 'файла', 'файлов'][plural(count.files)]
            }`;
        }
        text = `${foldersText}${and}${filesText}`;
    }

    renderComfirmationDialog({
        dataQAId: 'move-files-confirmation-dialog',
        renderHeader: () => 'Перемещение',
        successButtonText: 'Переместить',
        renderContent: () => (
            <>
                <div style={{ overflowWrap: 'break-word' }}>{`Переместить ${text} в папку «${destination.name}»?`}</div>
                {isSharedOrMounted && (
                    <div style={{ overflowWrap: 'break-word', marginTop: '16px', fontWeight: 'normal' }} data-name="alert-text">
                        После переноса файлы будут доступны всем, у кого есть доступ к этой папке.
                    </div>
                )}
            </>
        ),
        onSuccess: () => reduxStore.dispatch(moveRequest({ items, destination: destination.id })),
        onClose: noop,
    });
};

export const sendAgreement = (suffix = '') => app.sendAgreement(suffix);

export const openInNewTab = ({ id, storage }) => {
    let url;
    if (typeof app.buildHref === 'function') {
        url = app.buildHref({ id, storage });
    } else {
        const path = storage === EStorageType.feed && storage;
        url = `/${path}/${encodeURIComponent(id)}`;
    }
    opener(url);
};

export const updateRollContent = (data) => {
    try {
        app.Rubles?.updateRollContent(data);
    } catch (error) {
        captureException(error);
    }
};

export const updateWorm = (data?: any) => {
    try {
        app.Rubles?.updateWorm(data);
    } catch (error) {
        captureException(error);
    }
};

export const updateViewerAttachesWorm = (expId: number) => {
    try {
        app.Rubles?.updateViewerAttachesWorm(expId);
    } catch (error) {
        captureException(error);
    }
};

export const updateAdvAfterDatalist = (width: number | null = null) => {
    try {
        app.Rubles?.updateAdvAfterDatalist(width);
    } catch (error) {
        captureException(error);
    }
};

export const updateAdvPhonePublicInsideDatalist = () => {
    try {
        app.Rubles?.updateAdvPhonePublicInsideDatalist();
    } catch (error) {
        captureException(error);
    }
};

export const updateErrorPageRightColAdv = (siteZone: number) => {
    try {
        app.Rubles?.updateErrorPageRightColAdv(siteZone);
    } catch (error) {
        captureException(error);
    }
};

export const updateErrorPagePhoneTopAdv = (siteZone: number) => {
    try {
        app.Rubles?.updateErrorPagePhoneTopAdv(siteZone);
    } catch (error) {
        captureException(error);
    }
};

export const removeItemsWithoutDialog = ({ items }) =>
    reduxStore.dispatch(
        removeRequest({
            items,
            withRemoveDialog: false,
        })
    );

// Удаление файлов с окном подтверждения
export const removeItems = ({ items, onSuccess = noopVoid }) => {
    reduxStore.dispatch(removeRequest({ items, withRemoveDialog: true, onSuccess }));
};

export const updateIndiPublicWorm = () => {
    try {
        app.Rubles?.updateIndiPublicWorm();
    } catch (error) {
        captureException(error);
    }
};

export const updateViewerPublicBottomWorm = () => {
    try {
        app.Rubles?.updateViewerPublicBottomWorm();
    } catch (error) {
        captureException(error);
    }
};

export const update404Content = () => {
    try {
        app.Rubles?.update404Content();
    } catch (error) {
        captureException(error);
    }
};

export const getInitialId = () => app.getState().id;

export const canCloneWeblink = (isAuthorized: boolean, storage: EStorageType | null = EStorageType.home, isCheckCloudOnPublic = false) => {
    const { isPublic, isStock, isAttaches } = getStorage(storage || EStorageType.home);
    const isOwn = config.get('IS_OWN_PUBLIC');
    const isCloudAvailabe = (IS_CLOUD_CREATED || !isAuthorized) && !IS_CLOUD_BANNED && !IS_CLOUD_LOCKED;
    const enableClone = isCheckCloudOnPublic
        ? (isPublic || isStock || isAttaches) && isCloudAvailabe
        : isPublic || isStock || (isAttaches && isCloudAvailabe);

    return enableClone && (isStock || !isOwn);
};

export const openShareDialog = ({ item }) => reduxStore.dispatch(openPublishDialog({ item, from: EFrom.SHARING }));

export const createAutoDeleteFolder = () => reduxStore.dispatch(createNewAutoDeleteFolder());

export const getIsCloudAvailabe = () => IS_CLOUD_CREATED && !IS_CLOUD_BANNED && !IS_CLOUD_LOCKED;

export const isModifiedSharing = (file?: PublishItem | null) =>
    isModifiedSharingWindow && isWopiCoEdit && !EnvironmentSelectors.isPhone() && !file?.isFolder;
